import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, paymentApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { planAllowsAnotherSeat } from '@shared/constants/index.ts';
import { Button } from '../ui.tsx';
import { FounderContactLine } from '../founder-contact.tsx';

/**
 * "What should I set up next?" — the question the settings page never
 * answered. A new company faces a dozen cards, five of which hide a
 * connect-something-external flow behind them, and the only way to discover
 * any of it was to scroll.
 *
 * The guide lists the setup steps whose state the app can actually observe —
 * whether anybody has said what kind of cleaning this company does, a logo
 * uploaded, a processor connected, the quote form enabled, text reminders on,
 * a second team member — and jumps to the card that does each one. Steps with
 * sensible defaults (pricing, working hours) are deliberately absent: the app
 * cannot tell "reviewed and kept" from "never looked", and a checklist that
 * cannot be completed teaches people to ignore it.
 *
 * The service-mode step is in it precisely because that distinction IS
 * recorded for this one setting — `serviceModeChosenAt` — and because the
 * answer decides what the rest of the app assumes.
 *
 * It hides itself once every step is done, and can be dismissed early —
 * a company that will never text customers should not be nagged about it
 * forever. The dismissal is per-browser (localStorage): wrong for the one
 * owner who uses two machines, but a settings row for "hide the settings
 * guide" would be exactly the clutter this card exists to reduce.
 */

function dismissalKey(companyId: string) {
  return `settings-setup-guide-hidden:${companyId}`;
}

function readDismissed(companyId: string): boolean {
  try {
    return window.localStorage.getItem(dismissalKey(companyId)) === '1';
  } catch {
    return false; // Storage blocked (private mode) — just show the guide.
  }
}

interface SetupItem {
  id: string;
  done: boolean;
  /** The settings section the "Set it up" button jumps to. */
  target: string;
  /**
   * Interpolated into this step's title and description. The service-mode
   * step names the mode the company is on right now, so the guide states
   * what the app currently assumes rather than only asking about it.
   */
  values?: Record<string, string>;
}

export default function SetupGuide({ onJump }: { onJump: (sectionId: string) => void }) {
  const { t } = useTranslation();
  const { company, can } = useSession();
  const [dismissed, setDismissed] = useState(() => (company ? readDismissed(company.id) : false));

  // Permission AND plan: a step that jumps to a card this plan does not have
  // sends somebody to a section the page no longer renders, and its query
  // would answer 403 — which, since the guide waits for a successful read
  // before rendering at all, would hide the guide from a Free company
  // permanently rather than showing it the steps it CAN do.
  const hasInvoicing = usePlanFeature('invoicing');
  const hasOnlineIntake = usePlanFeature('online_intake');
  const hasSms = usePlanFeature('sms');
  const canPayments = can('payments:read') && hasInvoicing;
  // Free covers one user, so "invite your team" is not a step it can finish.
  const canTeam = can('users:manage') && planAllowsAnotherSeat(company?.effectivePlan ?? 'business', 1);

  // Both keys are shared with the cards further down the page, so the guide
  // costs nothing extra once either section has loaded — and vice versa.
  const payments = useQuery({
    queryKey: ['payments-status'],
    queryFn: () => paymentApi.status(),
    enabled: canPayments && !dismissed,
  });
  const users = useQuery({
    queryKey: ['company-users'],
    queryFn: () => companyApi.users(),
    enabled: canTeam && !dismissed,
  });

  // Owners only: every step needs company:write to act on.
  if (!company || !can('company:write') || dismissed) return null;
  // Not yet knowable is not "not done". Rendering the guide before these
  // answer would flash it at every fully-set-up company on every visit.
  if (canPayments && !payments.isSuccess) return null;
  if (canTeam && !users.isSuccess) return null;

  const smsSendable = company.smsConfigured ?? false;
  const items: SetupItem[] = [
    /*
      First, because everything below it is set up differently depending on
      the answer, and because it is the one step whose "not done" state is
      invisible everywhere else: a company that was never asked reads as
      residential, quotes as residential, and shows no sign that the
      commercial half of the app exists.

      Done is the stamp, never the mode — 'residential' is both a real answer
      and the default for a company that predates the question, and a step
      that treated the default as an answer would tick itself for exactly the
      companies that most need to be asked.
    */
    {
      id: 'serviceMode',
      done: Boolean(company.serviceModeChosenAt),
      target: 'company',
      values: { mode: t(`settings.serviceMode.${company.serviceMode}`) },
    },
    { id: 'logo', done: Boolean(company.logoUrl), target: 'company' },
    ...(canPayments ? [{ id: 'payments', done: Boolean(payments.data?.configured), target: 'payments' }] : []),
    ...(hasOnlineIntake ? [{ id: 'widget', done: Boolean(company.intakePath), target: 'widget' }] : []),
    ...(hasSms
      ? [
          {
            id: 'sms',
            done: smsSendable && company.smsRemindersEnabled,
            // With texting available, the switch is on the reminders card;
            // without, the first move is connecting an account on the
            // texting setup page.
            target: smsSendable ? 'reminders' : 'texting',
          },
        ]
      : []),
    ...(canTeam ? [{ id: 'team', done: (users.data?.items.length ?? 0) > 1, target: 'team' }] : []),
  ];

  const done = items.filter((item) => item.done).length;
  if (done === items.length) return null;

  function dismiss() {
    setDismissed(true);
    try {
      if (company) window.localStorage.setItem(dismissalKey(company.id), '1');
    } catch {
      // Storage blocked — the state above still hides it for this visit.
    }
  }

  return (
    <section className="card mb-6" aria-labelledby="setup-guide-heading">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="setup-guide-heading" className="text-lg font-semibold">
          {t('settings.setupGuide.heading')}
        </h2>
        <p className="text-sm text-[color:var(--color-muted)]">
          {t('settings.setupGuide.progress', { done, total: items.length })}
        </p>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.setupGuide.description')}</p>

      <ul className="divide-y divide-[color:var(--color-line)]">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3 py-3">
            <span
              aria-hidden="true"
              className={
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ' +
                (item.done
                  ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                  : 'border-[color:var(--color-line)] text-transparent')
              }
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 12l5 5L20 6" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className={'text-sm font-medium ' + (item.done ? 'text-[color:var(--color-muted)] line-through' : '')}>
                {t(`settings.setupGuide.items.${item.id}.title`, item.values ?? {})}
                {item.done ? <span className="sr-only"> — {t('settings.setupGuide.done')}</span> : null}
              </p>
              {item.done ? null : (
                <p className="text-sm text-[color:var(--color-muted)]">
                  {t(`settings.setupGuide.items.${item.id}.description`, item.values ?? {})}
                </p>
              )}
            </div>
            {item.done ? null : (
              <Button
                onClick={() => onJump(item.target)}
                aria-label={t('settings.setupGuide.goAria', {
                  step: t(`settings.setupGuide.items.${item.id}.title`),
                })}
              >
                {t('settings.setupGuide.go')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      <FounderContactLine className="mt-3 text-sm text-[color:var(--color-muted)]" />

      <div className="mt-3 border-t border-[color:var(--color-line)] pt-3 text-right">
        <button
          type="button"
          className="text-sm font-medium text-[color:var(--color-muted)] hover:underline"
          onClick={dismiss}
        >
          {t('settings.setupGuide.hide')}
        </button>
      </div>
    </section>
  );
}
